import React, { useState, useEffect } from 'react';
import { getBillingItems } from '../../services/api';
import { DollarSign, Search } from 'lucide-react';

const typeConfig = {
  setup:     { text: 'Paramétrage',  color: 'bg-purple-100 text-purple-700' },
  reception: { text: 'Réception',    color: 'bg-blue-100 text-blue-700' },
  order:     { text: 'Commande',     color: 'bg-orange-100 text-orange-700' },
  other:     { text: 'Autre',        color: 'bg-slate-100 text-slate-700' },
};

const statusConfig = {
  to_invoice: { text: 'A facturer',  color: 'bg-amber-100 text-amber-700' },
  invoiced:   { text: 'Facturé',     color: 'bg-blue-100 text-blue-700' },
  paid:       { text: 'Payé',        color: 'bg-emerald-100 text-emerald-700' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatAmount = (val) => {
  if (val === null || val === undefined) return null;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
};

const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
};

const ClientInvoices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');

  const monthOptions = getMonthOptions();

  useEffect(() => {
    loadItems();
  }, [filterType, filterMonth]);

  const loadItems = async () => {
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterMonth) params.month = filterMonth;
      const res = await getBillingItems(params);
      setItems(res.data);
    } catch (e) {
      console.error('Erreur:', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(item =>
    (item.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sumByStatus = (status) => filtered.filter(i => i.status === status).reduce((s, i) => s + (i.amount || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center min-h-96 text-slate-500">Chargement...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Mes Prestations</h1>
        <p className="text-slate-500">{filtered.length} prestations</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <DollarSign className="text-amber-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{formatAmount(sumByStatus('to_invoice')) || '0 €'}</div>
            <div className="text-sm text-slate-500">A facturer</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <DollarSign className="text-blue-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{formatAmount(sumByStatus('invoiced')) || '0 €'}</div>
            <div className="text-sm text-slate-500">Facturé - en attente</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <DollarSign className="text-emerald-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{formatAmount(sumByStatus('paid')) || '0 €'}</div>
            <div className="text-sm text-slate-500">Payé</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-64 flex items-center gap-3 bg-white px-4 py-2.5 rounded-lg border border-slate-200">
          <Search size={18} className="text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher référence, code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-sm"
          />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm">
          <option value="">Tous les types</option>
          <option value="setup">Paramétrage</option>
          <option value="reception">Réception</option>
          <option value="order">Commande</option>
          <option value="other">Autre</option>
        </select>
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm">
          <option value="">Toutes les périodes</option>
          {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Référence</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Code prestation</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Montant</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const typeCfg = typeConfig[item.type] || typeConfig.other;
                const statusCfg = statusConfig[item.status] || statusConfig.to_invoice;
                return (
                  <tr key={item.id} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <td className="px-4 py-3 text-sm font-mono text-slate-700">{item.reference}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeCfg.color}`}>{typeCfg.text}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{item.code || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(item.date)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      {item.amount != null
                        ? <span className="text-slate-800">{formatAmount(item.amount)}</span>
                        : <span className="text-slate-400 italic">A définir</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>{statusCfg.text}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-12 text-center">
            <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Aucune prestation trouvée</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientInvoices;
